import { useEffect, useState } from 'react'

// @mui
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Switch from '@mui/material/Switch'
import Icon from '@mui/material/Icon'
import Divider from '@mui/material/Divider'

// MD
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'

// Layout
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Loader from 'examples/Loader'

// Services
import { useAppServices } from 'hook/services'
import { setNotification, useNotifcation } from 'context/notification'

const KIND_TABS = [
  { key: 'profession', label: 'Professions' },
  { key: 'city', label: 'Cities' },
]

function DirectoryOptions() {
  const AppService = useAppServices()
  const [, dispatchNotif] = useNotifcation()

  const [kind, setKind] = useState('city')
  const [loading, setLoading] = useState(true)
  const [options, setOptions] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [newValue, setNewValue] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editingValue, setEditingValue] = useState('')

  const notify = (msg, type = 'success') =>
    setNotification(dispatchNotif, { open: true, message: msg, severity: type })

  const fetchAll = async (k = kind) => {
    setLoading(true)
    try {
      const [optRes, sugRes] = await Promise.all([
        AppService.directoryOptions.listAdmin({ query: `kind=${k}` }),
        AppService.directoryOptions.otherSuggestions({ query: `kind=${k}` }),
      ])
      setOptions(optRes?.response?.success ? optRes.response.data || [] : [])
      setSuggestions(sugRes?.response?.success ? sugRes.response.data || [] : [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll(kind)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind])

  const handleAdd = async () => {
    const value = newValue.trim()
    if (!value) return
    const { response } = await AppService.directoryOptions.create({
      payload: { kind, value },
    })
    if (response?.success) {
      setNewValue('')
      notify(response.message || 'Added.')
      fetchAll()
    } else {
      notify(response?.message || 'Could not add option.', 'error')
    }
  }

  const handleToggle = async (opt) => {
    const { response } = await AppService.directoryOptions.update({
      params: { id: opt._id },
      payload: { active: !opt.active },
    })
    if (response?.success) {
      setOptions((prev) =>
        prev.map((o) => (o._id === opt._id ? { ...o, active: !opt.active } : o))
      )
    } else {
      notify(response?.message || 'Could not update.', 'error')
    }
  }

  const handleSaveEdit = async (opt) => {
    const value = editingValue.trim()
    if (!value || value === opt.value) {
      setEditingId(null)
      return
    }
    const { response } = await AppService.directoryOptions.update({
      params: { id: opt._id },
      payload: { value },
    })
    if (response?.success) {
      setEditingId(null)
      notify('Renamed.')
      fetchAll()
    } else {
      notify(response?.message || 'Could not rename.', 'error')
    }
  }

  const handleDelete = async (opt) => {
    if (!window.confirm(`Delete "${opt.value}"? This won't delete existing listings.`)) return
    const { response } = await AppService.directoryOptions.remove({
      params: { id: opt._id },
    })
    if (response?.success) {
      notify('Deleted.')
      fetchAll()
    } else {
      notify(response?.message || 'Could not delete.', 'error')
    }
  }

  const handlePromote = async (value) => {
    const { response } = await AppService.directoryOptions.promote({
      payload: { kind, value },
    })
    if (response?.success) {
      notify(response.message || 'Promoted.')
      fetchAll()
    } else {
      notify(response?.message || 'Could not promote.', 'error')
    }
  }

  const kindLabel = kind === 'city' ? 'city' : 'profession'

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading ? (
        <Loader />
      ) : (
        <MDBox pt={6} pb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    Directory Options
                  </MDTypography>
                  <MDTypography variant="caption" color="white">
                    Controls the dropdowns on the Creative Directory join form.
                  </MDTypography>
                </MDBox>

                <MDBox px={3} pt={2}>
                  <Tabs value={kind} onChange={(_, v) => setKind(v)}>
                    {KIND_TABS.map((t) => (
                      <Tab key={t.key} value={t.key} label={t.label} />
                    ))}
                  </Tabs>
                </MDBox>

                <MDBox p={3}>
                  <Grid container spacing={3}>
                    {/* Managed list */}
                    <Grid item xs={12} md={7}>
                      <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
                        Active list
                      </MDTypography>
                      <MDTypography variant="caption" color="text" mb={2} display="block">
                        Inactive options stay in the database but won't show on the join form.
                      </MDTypography>

                      <MDBox display="flex" gap={1} mb={2}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={`Add a ${kindLabel}…`}
                          value={newValue}
                          onChange={(e) => setNewValue(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                        />
                        <MDButton color="info" onClick={handleAdd} disabled={!newValue.trim()}>
                          Add
                        </MDButton>
                      </MDBox>

                      <MDBox>
                        {options.length === 0 ? (
                          <MDTypography variant="body2" color="text">
                            No options yet.
                          </MDTypography>
                        ) : (
                          options.map((opt) => (
                            <MDBox
                              key={opt._id}
                              display="flex"
                              alignItems="center"
                              gap={1}
                              py={1}
                              borderBottom="1px solid #eee"
                            >
                              {editingId === opt._id ? (
                                <>
                                  <TextField
                                    autoFocus
                                    size="small"
                                    fullWidth
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveEdit(opt)
                                      if (e.key === 'Escape') setEditingId(null)
                                    }}
                                  />
                                  <MDButton size="small" color="info" onClick={() => handleSaveEdit(opt)}>
                                    Save
                                  </MDButton>
                                  <MDButton size="small" onClick={() => setEditingId(null)}>
                                    Cancel
                                  </MDButton>
                                </>
                              ) : (
                                <>
                                  <MDBox flex={1}>
                                    <MDTypography variant="body2" fontWeight="medium">
                                      {opt.value}
                                    </MDTypography>
                                  </MDBox>
                                  <Tooltip title={opt.active ? 'Active' : 'Inactive'}>
                                    <Switch
                                      size="small"
                                      checked={!!opt.active}
                                      onChange={() => handleToggle(opt)}
                                    />
                                  </Tooltip>
                                  <Tooltip title="Rename">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setEditingId(opt._id)
                                        setEditingValue(opt.value)
                                      }}
                                    >
                                      <Icon fontSize="small">edit</Icon>
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" onClick={() => handleDelete(opt)}>
                                      <Icon fontSize="small">delete</Icon>
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </MDBox>
                          ))
                        )}
                      </MDBox>
                    </Grid>

                    {/* "Other" suggestions */}
                    <Grid item xs={12} md={5}>
                      <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
                        From "Other" submissions
                      </MDTypography>
                      <MDTypography variant="caption" color="text" mb={2} display="block">
                        What creatives have been typing when they pick "Other". Promote a value
                        to add it to the list and reassign matching listings to it.
                      </MDTypography>

                      <Divider />

                      {suggestions.length === 0 ? (
                        <MDBox py={2}>
                          <MDTypography variant="body2" color="text">
                            No "Other" submissions yet.
                          </MDTypography>
                        </MDBox>
                      ) : (
                        suggestions.map((s) => (
                          <MDBox
                            key={s.value + s.count}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            py={1.25}
                            borderBottom="1px solid #eee"
                          >
                            <MDBox>
                              <MDTypography variant="body2" fontWeight="medium">
                                {s.value}
                              </MDTypography>
                              <Chip
                                size="small"
                                label={`${s.count} listing${s.count === 1 ? '' : 's'}`}
                                sx={{ mt: 0.5 }}
                              />
                            </MDBox>
                            <MDButton
                              size="small"
                              color="success"
                              variant="gradient"
                              onClick={() => handlePromote(s.value)}
                            >
                              Add to list
                            </MDButton>
                          </MDBox>
                        ))
                      )}
                    </Grid>
                  </Grid>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      )}
    </DashboardLayout>
  )
}

export default DirectoryOptions
